import { Form } from 'antd';
import { useContext, useEffect, useState } from 'react';

import { CurrencyContext } from 'app/components/commons/Currency/CurrencyContext/CurrencyContext';
import { HotelContext } from 'app/context/HotelContext/HotelContext';
import { useDiscountAllocation } from 'app/hooks/data/core/extranet/pack/useDiscountAllocation';
import { DAY_OF_WEEK } from 'app/typings/core/common/enum/dayOfWeek';
import type { DayOfWeek } from 'app/typings/core/common/enum/dayOfWeek';

export type DefaultDiscountMode = 'CONSTANT' | 'DAILY';

type RoomDiscountFormValues = {
  averageRoomPrice?: number | null;
  defaultDiscountMode?: DefaultDiscountMode;
  defaultNightRoomDiscount?: number | null;
  defaultNightRoomDiscountDays?: Partial<Record<DayOfWeek, number>> | null;
  lastMinuteNightRoomDiscountRate?: number | null;
  lastMinuteNightRoomDiscountDays?: number | null;
};

export const useRoomDiscountLogic = () => {
  const { hotelId, hotelInfo } = useContext(HotelContext);
  const { currencyFormatter } = useContext(CurrencyContext);
  const [isLastMinuteEnabled, setIsLastMinuteEnabled] = useState(false);

  useEffect(() => {
    setIsLastMinuteEnabled(
      hotelInfo?.lastMinuteNightRoomDiscountRate !== null &&
        hotelInfo?.lastMinuteNightRoomDiscountRate !== undefined &&
        hotelInfo?.lastMinuteNightRoomDiscountDays !== null &&
        hotelInfo?.lastMinuteNightRoomDiscountDays !== undefined
    );
  }, [hotelInfo]);

  const form = Form.useFormInstance<RoomDiscountFormValues>();
  const formRoomPrice = Form.useWatch('averageRoomPrice', form);
  const discountMode = Form.useWatch('defaultDiscountMode', form) ?? 'CONSTANT';
  const defaultNightRoomDiscount = Form.useWatch(
    'defaultNightRoomDiscount',
    form
  );
  const defaultNightRoomDiscountDays = Form.useWatch(
    'defaultNightRoomDiscountDays',
    form
  );
  const lastMinuteNightRoomDiscountRate = Form.useWatch(
    'lastMinuteNightRoomDiscountRate',
    form
  );

  const { data: averageRoomPrice } = useDiscountAllocation(hotelId, true);

  const getDayBasePrice = (day: DayOfWeek): number | null | undefined => {
    if (formRoomPrice === null || formRoomPrice === undefined) {
      return formRoomPrice;
    }

    const apiAverage = averageRoomPrice?.average;
    const apiDayPrice = averageRoomPrice?.days?.[day];

    if (
      apiDayPrice === null ||
      apiDayPrice === undefined ||
      apiAverage === null ||
      apiAverage === undefined ||
      apiAverage === 0
    ) {
      return formRoomPrice;
    }

    return (formRoomPrice * apiDayPrice) / apiAverage;
  };

  const handleDiscountModeChange = (mode: DefaultDiscountMode) => {
    if (
      mode === 'DAILY' &&
      defaultNightRoomDiscount !== null &&
      defaultNightRoomDiscount !== undefined
    ) {
      const currentDays = form.getFieldValue('defaultNightRoomDiscountDays') as
        | Partial<Record<DayOfWeek, number>>
        | null
        | undefined;

      const filledDays = Object.values(DAY_OF_WEEK).reduce((acc, day) => {
        acc[day] = currentDays?.[day] ?? defaultNightRoomDiscount;

        return acc;
      }, {} as Partial<Record<DayOfWeek, number>>);

      form.setFieldsValue({
        defaultDiscountMode: mode,
        defaultNightRoomDiscountDays: filledDays,
      });

      return;
    }

    form.setFieldsValue({ defaultDiscountMode: mode });
  };

  const handleLastMinuteToggle = (value: boolean) => {
    setIsLastMinuteEnabled(value);

    if (!value) {
      form.setFieldsValue({
        lastMinuteNightRoomDiscountRate: undefined,
        lastMinuteNightRoomDiscountDays: undefined,
      });
    }
  };

  return {
    currencyFormatter,
    form,
    formRoomPrice,
    discountMode,
    defaultNightRoomDiscount,
    defaultNightRoomDiscountDays,
    getDayBasePrice,
    handleDiscountModeChange,
    lastMinuteNightRoomDiscountRate,
    isLastMinuteEnabled,
    handleLastMinuteToggle,
  };
};
